<template>
	<div class="min-h-screen bg-white">
		<!-- Hero Section - Full-width image with text below -->
		<section>
			<div class="max-w-4xl mx-auto px-4 sm:px-6 pt-10 md:pt-6 text-center">
				<h1
					v-motion
					:initial="{ opacity: 0, y: 20 }"
					:enter="{ opacity: 1, y: 0, transition: { delay: 200 } }"
					class="text-4xl md:text-5xl lg:text-6xl text-ihh-midnight-gray mb-4"
					style="font-family: 'Cormorant Garamond', serif; font-weight: 500"
				>
					Our Story
				</h1>
				<p v-motion :initial="{ opacity: 0, y: 20 }" :enter="{ opacity: 1, y: 0, transition: { delay: 300 } }" class="text-lg text-ihh-slate-gray mb-8">
					Owned and operated by the Youngblood family in central&nbsp;Texas&nbsp;since&nbsp;1999
				</p>
			</div>
			<div
				v-motion
				:initial="{ opacity: 0 }"
				:enter="{ opacity: 1, transition: { duration: 800 } }"
				class="w-3/4 mx-auto aspect-[4/3] sm:aspect-[16/9] lg:aspect-[8/4] overflow-hidden"
			>
				<img src="/images/about-us/Youngblood_Family.webp" alt="Youngblood family photo" class="w-full h-full object-cover" />
			</div>
			<div class="max-w-4xl mx-auto px-4 sm:px-6 py-10 md:py-16 lg:py-20 text-center">
				<div class="space-y-6 text-left lg:text-center">
					<p v-motion :initial="{ opacity: 0, y: 20 }" :enter="{ opacity: 1, y: 0, transition: { delay: 500 } }" class="text-lg text-ihh-slate-gray leading-relaxed">
						For over 25 years, <strong>In His Hands Birth Supply</strong> has been owned and operated by the <strong>Youngblood family</strong>. Founded in the
						early 1990s, the company has steadily grown to become a <strong>premier provider of home birth supplies</strong> in the United States.
					</p>
					<p v-motion :initial="{ opacity: 0, y: 20 }" :enter="{ opacity: 1, y: 0, transition: { delay: 600 } }" class="text-lg text-ihh-slate-gray leading-relaxed">
						From our home office in <strong>Liberty Hill, Texas</strong>, we provide excellent service to expectant families, midwives, and other healthcare
						providers across the country. What began in a small room above our garage—serving just a few dozen midwives—has blossomed into a
						<strong>trusted, nationwide resource</strong> still owned & operated by <strong>Jack & Sherry Youngblood & family</strong>.
					</p>
				</div>
			</div>
		</section>

		<!-- Services Section - Offset Grid -->
		<section class="bg-gradient-to-b from-ihh-pink to-ihh-purple py-16 md:py-20 lg:py-24">
			<div class="max-w-7xl mx-auto px-6">
				<div class="grid grid-cols-12 gap-8 lg:gap-12 items-center">
					<div
						v-motion
						:initial="{ opacity: 0, x: -50 }"
						:visibleOnce="{ opacity: 1, x: 0, transition: { duration: 600 } }"
						class="col-span-12 md:col-span-6 lg:col-span-5 lg:col-start-2"
					>
						<div class="rounded-sm overflow-hidden">
							<img src="/images/about-us/custom_birthkit_service.webp" alt="Custom birth kit service" class="w-full h-auto" />
						</div>
					</div>
					<div
						v-motion
						:initial="{ opacity: 0, y: 30 }"
						:visibleOnce="{ opacity: 1, y: 0, transition: { delay: 200, duration: 600 } }"
						class="col-span-12 md:col-span-6 lg:col-span-5 lg:col-start-7 space-y-6"
					>
						<p class="text-lg text-ihh-slate-gray leading-relaxed">
							Today at In His Hands we offer a <strong>custom birth kit service</strong> for hundreds of midwives. In addition, we sell a wide selection of
							<strong>birth supplies</strong>, <strong>medical equipment</strong>, <strong>diagnostic tools</strong>, <strong>natural remedies</strong>,
							<strong>birth pools</strong>, and much more.
						</p>
						<p class="text-lg text-ihh-slate-gray leading-relaxed">
							While our business has grown, <strong>our mission remains the same</strong>. We're still a <strong>family-run business</strong>, with that family
							feel woven into everything we do. Whether you give us a call, chat online, or stop by for a local pickup, you'll be met with the same kind,
							helpful, can-do attitude that's earned our customers' trust for decades.
						</p>
						<p class="text-lg text-ihh-slate-gray leading-relaxed">
							We are known for our <strong>super-fast shipping</strong>, <strong>competitive pricing</strong>, and <strong>excellent customer service</strong>.
							We also offer a diverse and carefully selected <strong>product collection</strong> designed to meet the unique needs of both mothers and midwives.
						</p>
					</div>
				</div>
			</div>
		</section>

		<!-- Herbal Afterbirth Bath Section -->
		<section class="py-16 md:py-20 lg:py-24">
			<div class="max-w-7xl mx-auto px-6">
				<div class="grid grid-cols-12 gap-8 lg:gap-12 items-center">
					<div
						v-motion
						:initial="{ opacity: 0, x: -30 }"
						:visibleOnce="{ opacity: 1, x: 0, transition: { duration: 600 } }"
						class="col-span-12 md:col-span-7 lg:col-span-6 lg:col-start-2 space-y-6"
					>
						<p class="text-lg text-ihh-slate-gray leading-relaxed">
							One of our most beloved offerings, the
							<router-link
								to="/products/herbal-afterbirth-sitz-bath-dry-herb-soak-with-salt-12oz"
								class="text-ihh-dark-salmon underline hover:text-ihh-midnight-gray transition-colors font-semibold"
								>Herbal Afterbirth Bath</router-link
							>, was created by <strong>Sherry Youngblood</strong> herself. This gentle, healing blend of herbs has become a
							<strong>flagship product</strong>—offering new mothers and their babies a peaceful & healing soak after birth.
						</p>
					</div>
					<div class="col-span-12 md:col-span-5 lg:col-span-4">
						<div
							v-motion
							:initial="{ opacity: 0, x: 50 }"
							:visibleOnce="{ opacity: 1, x: 0, transition: { delay: 200, duration: 600 } }"
							class="aspect-[3/4] max-w-xs sm:max-w-sm mx-auto md:max-w-none rounded-sm overflow-hidden"
						>
							<router-link to="/products/herbal-afterbirth-sitz-bath-dry-herb-soak-with-salt-12oz">
								<img src="/images/herbal_after_birth_bath.jpg" alt="Herbal Afterbirth Bath" class="w-full h-full object-cover" />
							</router-link>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Labor Prayer Card Section - Asymmetric -->
		<section class="bg-magnolia-50 py-16 md:py-20 lg:py-24">
			<div class="max-w-7xl mx-auto px-6">
				<div class="grid grid-cols-12 gap-8 lg:gap-12 items-center">
					<div
						v-motion
						:initial="{ opacity: 0, scale: 0.95 }"
						:visibleOnce="{ opacity: 1, scale: 1, transition: { duration: 600 } }"
						class="col-span-12 md:col-span-5 lg:col-span-4 lg:col-start-2"
					>
						<div class="max-w-sm mx-auto md:max-w-none rounded-sm bg-white shadow-lg border border-gray-200 p-6 flex flex-col justify-center">
							<div class="border-t-2 border-b-2 border-ihh-dark-salmon py-4">
								<h3 class="text-center text-3xl text-ihh-midnight-gray mb-4" style="font-family: 'Brush Script MT', 'Segoe Script', cursive">Labor Prayer</h3>
								<div class="text-center text-sm text-ihh-slate-gray leading-relaxed space-y-3">
									<p>Dear Father, thank You for this new baby.</p>
									<p>Would You protect and nurture this baby and bring this pregnancy to full term.</p>
									<p>In labor, would You remove all fear and replace it with faith and expectation.</p>
									<p>Please give those attending the birth wisdom to take the best care of mother and baby.</p>
									<p>Please let this baby be born normal and whole, healthy and strong, and at home with no complications with baby or mother.</p>
									<p>
										Father, thank you for hearing my prayer, for I pray in the name of Him who is called Wonderful, Counselor, The Mighty God, The
										Everlasting Father, The Prince of Peace. Amen.
									</p>
								</div>
							</div>
						</div>
						<a
							href="/labor-prayer.pdf"
							download
							class="flex items-center justify-center gap-1.5 mt-4 text-sm text-ihh-dark-salmon hover:text-ihh-midnight-gray transition-colors w-full"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								class="w-4 h-4"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
							>
								<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
								<polyline points="7 10 12 15 17 10" />
								<line x1="12" y1="15" x2="12" y2="3" />
							</svg>
							Download PDF
						</a>
					</div>
					<div
						v-motion
						:initial="{ opacity: 0, x: 30 }"
						:visibleOnce="{ opacity: 1, x: 0, transition: { delay: 200, duration: 600 } }"
						class="col-span-12 md:col-span-7 lg:col-span-5 lg:col-start-7 flex flex-col justify-center"
					>
						<p class="text-lg text-ihh-slate-gray leading-relaxed">
							As a <strong>Christian company rooted in faith</strong>, we include a <strong>Labor Prayer Card</strong> in every birth kit. Written by In His
							Hands founder <strong>Donna Wilson</strong>, this simple yet powerful prayer has encouraged thousands of mothers during one of life's most sacred
							moments.
						</p>
					</div>
				</div>
			</div>
		</section>

		<!-- Three Generations Section -->
		<section class="bg-magnolia-50 py-16 md:py-20 lg:py-24">
			<div class="max-w-7xl mx-auto px-6">
				<div class="grid grid-cols-12 gap-8 lg:gap-12 items-center">
					<div
						v-motion
						:initial="{ opacity: 0, x: -30 }"
						:visibleOnce="{ opacity: 1, x: 0, transition: { duration: 600 } }"
						class="col-span-12 md:col-span-7 lg:col-span-5 lg:col-start-2 flex flex-col justify-center order-2 md:order-1"
					>
						<blockquote class="border-r-4 border-ihh-dark-salmon pr-6 py-4 text-right">
							<p class="text-lg md:text-xl lg:text-2xl text-ihh-midnight-gray leading-relaxed font-light italic">
								Today, with three generations of Youngbloods working in the business, we are delighted to serve mothers and midwives across the U.S. &
								worldwide. We remain committed to our roots, our faith, and our purpose: <strong>to be the best source for all things birth</strong>.
							</p>
						</blockquote>
					</div>
					<div
						v-motion
						:initial="{ opacity: 0, x: 30 }"
						:visibleOnce="{ opacity: 1, x: 0, transition: { delay: 200, duration: 600 } }"
						class="col-span-12 md:col-span-5 lg:col-span-4 lg:col-start-8 order-1 md:order-2"
					>
						<div class="max-w-sm mx-auto md:max-w-none rounded-sm overflow-hidden">
							<img src="/images/about-us/three_generations.webp" alt="Three generations of the Youngblood family" class="w-full h-auto" />
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Welcome Video Section -->
		<section class="py-16 md:py-20 lg:py-24">
			<div class="max-w-7xl mx-auto px-6">
				<div class="grid grid-cols-12 gap-8">
					<div class="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3">
						<div
							v-motion
							:initial="{ opacity: 0, y: 40 }"
							:visibleOnce="{ opacity: 1, y: 0, transition: { duration: 600 } }"
							class="aspect-video rounded-sm overflow-hidden"
						>
							<iframe
								v-if="vimeoVideoId"
								:src="`https://player.vimeo.com/video/${vimeoVideoId}`"
								class="w-full h-full"
								frameborder="0"
								allow="autoplay; fullscreen; picture-in-picture"
								allowfullscreen
							></iframe>
							<div v-else class="w-full h-full flex items-center justify-center">
								<p class="text-ihh-slate-gray text-center px-6">Welcome Video</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Round Logo Section -->
		<!-- <section class="py-24">
			<div class="max-w-7xl mx-auto px-6 flex justify-center">
				<div class="ml-5 w-full max-w-[90px] md:min-w-[160px] md:max-w-[225px]">
					<router-link to="/" class="w-full">
						<div class="w-full">
							<Logo class="w-full" />
						</div>
					</router-link>
				</div>
			</div>
		</section> -->
	</div>
</template>

<script>
import Logo from '../partials/logo.vue'
export default {
	name: 'AboutUsNew',
	components: { Logo },
	data() {
		return {
			vimeoVideoId: '1026264453',
		}
	},
}
</script>
